/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-floating-promises, react-hooks/exhaustive-deps, @typescript-eslint/strict-boolean-expressions */

import { useRecoilValue } from 'recoil';
import { alpha, useTheme } from '@mui/material/styles';
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  List,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import SettingsPopout from '../../shared/SettingsPopout';
import { ZoomOutButton } from '../../shared';
import { DownloadChartButton } from '../../shared/DownloadChartButton';
import { DEFAULT_BRUSH_ZOOM_CONFIG } from 'config';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Dayjs } from 'dayjs';
import { currentStatsState } from 'states/iVol';
import { useCallback, useMemo, useState } from 'react';
import {
  getCurrentDate,
  getDefaultSlotProps,
  getQueryDate,
  isBusinessDay,
  isMarketOpenOnDate,
  nextBusinessDay,
  prevBusinessDayOpenMarket,
} from 'util/shared';
import useImpliedVolatility from 'hooks/iVol/useImpliedVolatility';
import {
  DailyGreeks,
  DashAccess,
  ExpirationsDisplayType,
  GreeksStatsLookback,
  hasDashAccess,
  RawGreeksDataMap,
  TermStructure,
} from 'types';
import { TimeFramePill } from '../TimeFramePill';
import {
  MIN_QUERY_DATE,
  extractTermStructureData,
  findClosestGreeksExp,
  findD50Price,
  getNextAvailableColor,
  getTermStructureKeyFromLabel,
  mergeObjectLists,
} from 'util/iVol';
import { timezoneState, userDashAccessState } from 'states';
import StatsLookbackSettings from '../StatsLookbackSetting';
import useClosingPrices from 'hooks/equityhub/useClosingPrices';
import { useTermStructureCtx } from './TermStructureContext';

const TermStructureControls = () => {
  const {
    selectedSym,
    config,
    updateConfig: onConfigChange,
    selectedDates,
    setSelectedDates: onSelectedDatesChange,
    loading,
    error,
  } = useTermStructureCtx();
  const theme = useTheme();
  const dashAccess = useRecoilValue(userDashAccessState);
  const currentStats = useRecoilValue(currentStatsState);
  const currentTimezone = useRecoilValue(timezoneState);

  const defaultSlotProps = getDefaultSlotProps(theme);
  const availableColors = Object.values(theme.palette.iVol.termStructure);

  const { getDailyGreeksData, getCurrentGreeksData } = useImpliedVolatility();
  const { getClosingPrice } = useClosingPrices();
  const maxQueryDate = getQueryDate(true);

  // Local date picker state
  const [selectedDate, setSelectedDate] = useState<Dayjs>(
    prevBusinessDayOpenMarket(getQueryDate(true)),
  );

  const {
    displayType: selectedDisplayType,
    showEconomicEvents,
    showForwardIVs,
    showStats: showTermStructureIVStats,
    useSpotGammaIV,
    statsLookback: currentStatsLookback,
    hiddenDates,
    additionalDates,
    showDividends,
  } = config;

  // Derive visible lines from hiddenDates
  const visibleChartLines = useMemo(
    () =>
      selectedDates.map((dg) => dg.key).filter((k) => !hiddenDates.includes(k)),
    [selectedDates, hiddenDates],
  );

  const usedLineColors = useMemo(
    () => selectedDates.map((dg) => dg.color),
    [selectedDates],
  );

  const onStatsLookbackChange = (newLookback: GreeksStatsLookback) => {
    if (!currentStats) {
      return;
    }
    // Rebuild term structure data with new lookback
    let finalData: TermStructure[] = [];
    selectedDates.forEach((dg: DailyGreeks) => {
      const rawGreeks: RawGreeksDataMap = dg.data;
      const tradeDate = dg.tradeDate;
      const key: string = tradeDate.format('YYYY-MM-DD');

      if (rawGreeks) {
        const transformedTermStructureData: TermStructure[] =
          extractTermStructureData(
            rawGreeks,
            tradeDate,
            key,
            currentStats[newLookback],
            dg.currentPrice,
            hasDashAccess(dashAccess, DashAccess.INTERNAL) && useSpotGammaIV,
          );

        finalData = mergeObjectLists(
          finalData,
          transformedTermStructureData,
          getTermStructureKeyFromLabel(selectedDisplayType),
        );
      }
    });
    // Note: chart will re-process when config.statsLookback changes
    onConfigChange({ statsLookback: newLookback });
  };

  const onAddNewTimeFrame = useCallback(
    async (dateToAdd: Dayjs) => {
      const tradeDate = dateToAdd.tz(currentTimezone);
      const formattedDate: string = tradeDate.format('YYYY-MM-DD');
      const isCurrentDay = tradeDate.isSame(getCurrentDate(), 'day');

      const [rawData, currentPrice] = await Promise.all([
        isCurrentDay
          ? getCurrentGreeksData(selectedSym)
          : getDailyGreeksData(formattedDate, selectedSym),
        getClosingPrice(selectedSym, isCurrentDay ? undefined : formattedDate),
      ]);

      if (rawData && currentStats) {
        const fwdPrice = findD50Price(
          rawData[findClosestGreeksExp(rawData, tradeDate)],
        );

        const color =
          getNextAvailableColor(usedLineColors, availableColors) ??
          theme.palette.primary.main;

        // Add to selectedDates (chart will re-render)
        onSelectedDatesChange((prev: DailyGreeks[]) => [
          ...prev,
          {
            key: formattedDate,
            tradeDate: tradeDate.isSame(getCurrentDate(), 'day')
              ? getCurrentDate(currentTimezone)
              : tradeDate,
            data: rawData,
            currentPrice: currentPrice || fwdPrice,
            color,
          },
        ]);

        // Persist to URL (including assigned color for reload)
        onConfigChange({
          additionalDates: [
            ...additionalDates,
            {
              key: formattedDate,
              tradeDate: tradeDate.isSame(getCurrentDate(), 'day')
                ? getCurrentDate(currentTimezone)
                : tradeDate,
              color,
              currentPrice: currentPrice || fwdPrice,
            },
          ],
        });

        // Advance date picker to previous business day
        if (
          prevBusinessDayOpenMarket(dateToAdd).isAfter(
            prevBusinessDayOpenMarket(MIN_QUERY_DATE),
            'day',
          )
        ) {
          setSelectedDate(prevBusinessDayOpenMarket(dateToAdd));
        }
      }
    },
    [
      currentTimezone,
      getCurrentGreeksData,
      selectedSym,
      getDailyGreeksData,
      getClosingPrice,
      currentStats,
      usedLineColors,
      onSelectedDatesChange,
      onConfigChange,
      additionalDates,
      setSelectedDate,
    ],
  );

  const onToggleLineVisiblity = useCallback(
    (key: string, toShow: boolean) => {
      onConfigChange({
        hiddenDates: toShow
          ? hiddenDates.filter((d: string) => d !== key)
          : [...hiddenDates, key],
      });

      // Also rebuild chart data for the updated visible lines
      if (currentStats) {
        const updatedVisible = toShow
          ? [...visibleChartLines, key]
          : visibleChartLines.filter((l: string) => l !== key);
        const updatedDailyGreeksArr = selectedDates.filter((dg: DailyGreeks) =>
          updatedVisible.includes(dg.key),
        );
        updatedTermStructureData(updatedDailyGreeksArr, selectedDisplayType);
      }
    },
    [
      hiddenDates,
      visibleChartLines,
      selectedDisplayType,
      selectedDates,
      currentStats,
    ],
  );

  const updatedTermStructureData = (
    dailyGreeksArr: DailyGreeks[],
    displayType: ExpirationsDisplayType,
  ) => {
    // This function exists for side-effect awareness by the chart.
    // The chart will re-process data when selectedDates or config changes.
    // No direct state update needed here since chart handles it.
    void dailyGreeksArr;
    void displayType;
  };

  const onChangeXDisplayType = (displayType: ExpirationsDisplayType) => {
    onConfigChange({ displayType });
  };

  const onDeleteTimeFrame = (key: string) => {
    onSelectedDatesChange((prev: DailyGreeks[]) =>
      prev.filter((dg: DailyGreeks) => dg.key !== key),
    );
    onConfigChange({
      additionalDates: additionalDates.filter((d) => d.key !== key),
      hiddenDates: hiddenDates.filter((d) => d !== key),
    });
  };

  const reachedMaximumDates = useMemo(
    () => selectedDates.length === availableColors.length,
    [selectedDates],
  );

  const isSelectedDateValid = useMemo(() => {
    return (
      selectedDate &&
      selectedDate.isValid() &&
      isBusinessDay(selectedDate) &&
      selectedDate.isAfter(prevBusinessDayOpenMarket(MIN_QUERY_DATE), 'day') &&
      selectedDate.isBefore(nextBusinessDay(maxQueryDate), 'day') &&
      !selectedDates.some((dailyGreeks: DailyGreeks) =>
        dailyGreeks.tradeDate.isSame(selectedDate, 'day'),
      )
    );
  }, [maxQueryDate, selectedDate, selectedDates]);

  const datePickerHelpText = useMemo(() => {
    if (reachedMaximumDates) {
      return 'Maximum number of dates used';
    }
    if (error) {
      return 'Failed to fetch data for selected date. Try again!';
    } else if (selectedDate.isBefore(MIN_QUERY_DATE, 'day')) {
      return `No data available for ${selectedDate.format(
        'YYYY-MM-DD',
      )}. Try another date!`;
    }
    return null;
  }, [error, reachedMaximumDates, selectedDate]);

  return (
    <Stack sx={{ textAlign: 'left' }} spacing={5}>
      <ButtonGroup
        aria-label="outlined secondary button group"
        sx={{ alignSelf: 'left', flexWrap: 'wrap' }}
      >
        {Object.values(ExpirationsDisplayType).map(
          (type: ExpirationsDisplayType) => (
            <Button
              key={type}
              sx={{
                fontFamily: 'SF Pro Display',
                backgroundColor:
                  selectedDisplayType === type
                    ? alpha(theme.palette.secondary.main, 0.25)
                    : 'transparent',
                '&:hover': {
                  color: alpha(theme.palette.secondary.main, 1),
                  borderColor: alpha(theme.palette.secondary.main, 1),
                },
                fontSize: 14,
                textTransform: 'none',
                width: '150px',
              }}
              onClick={() => {
                onChangeXDisplayType(type);
              }}
            >
              {type}
            </Button>
          ),
        )}
      </ButtonGroup>
      <FormControl component="fieldset">
        <FormGroup
          aria-label="form-control"
          sx={{
            width: '300px',
            justifyContent: 'space-between',
          }}
          row
        >
          <FormControlLabel
            sx={{ marginLeft: 0 }}
            control={
              <Switch
                sx={{
                  '& .MuiSwitch-track': {
                    opacity: 0.25,
                    backgroundColor: theme.palette.success.main,
                  },
                  '&.Mui-checked .MuiSwitch-track': { opacity: 1 },
                }}
                color="success"
                checked={showForwardIVs}
                onChange={(_e, checked: boolean) =>
                  onConfigChange({ showForwardIVs: checked })
                }
              />
            }
            label="Forward IV Adj."
            labelPlacement={
              selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                ? 'top'
                : 'start'
            }
          />
          <FormControlLabel
            sx={{ marginLeft: 0 }}
            control={
              <Switch
                sx={{
                  '& .MuiSwitch-track': {
                    opacity: 0.25,
                    backgroundColor: theme.palette.success.main,
                  },
                  '&.Mui-checked .MuiSwitch-track': { opacity: 1 },
                }}
                color="success"
                checked={showTermStructureIVStats}
                onChange={(_e, checked: boolean) =>
                  onConfigChange({ showStats: checked })
                }
              />
            }
            label="Statistics"
            labelPlacement={
              selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                ? 'top'
                : 'start'
            }
          />
          {selectedDisplayType === ExpirationsDisplayType.ExpirationDate && (
            <>
              <FormControlLabel
                sx={{
                  marginLeft: 0,
                }}
                control={
                  <Switch
                    sx={{
                      '& .MuiSwitch-track': {
                        opacity: 0.25,
                        backgroundColor: theme.palette.success.main,
                      },
                      '&.Mui-checked .MuiSwitch-track': {
                        opacity: 1,
                      },
                    }}
                    color="success"
                    checked={showEconomicEvents}
                    onChange={(_e, checked: boolean) =>
                      onConfigChange({ showEconomicEvents: checked })
                    }
                  />
                }
                label="Economic Events"
                labelPlacement="top"
              />
              {hasDashAccess(dashAccess, DashAccess.INTERNAL) && (
                <FormControlLabel
                  sx={{
                    marginLeft: 0,
                  }}
                  control={
                    <Switch
                      sx={{
                        '& .MuiSwitch-track': {
                          opacity: 0.25,
                          backgroundColor: theme.palette.success.main,
                        },
                        '&.Mui-checked .MuiSwitch-track': {
                          opacity: 1,
                        },
                      }}
                      color="success"
                      checked={showDividends}
                      onChange={(_e, checked: boolean) =>
                        onConfigChange({ showDividends: checked })
                      }
                    />
                  }
                  label="Dividends"
                  labelPlacement="top"
                />
              )}
            </>
          )}
          {hasDashAccess(dashAccess, DashAccess.INTERNAL) && (
            <FormControlLabel
              sx={{ marginLeft: 0 }}
              control={
                <Switch
                  sx={{
                    '& .MuiSwitch-track': {
                      opacity: 0.25,
                      backgroundColor: theme.palette.success.main,
                    },
                    '&.Mui-checked .MuiSwitch-track': { opacity: 1 },
                  }}
                  color="success"
                  checked={useSpotGammaIV}
                  onChange={(_e, checked: boolean) =>
                    onConfigChange({ useSpotGammaIV: checked })
                  }
                />
              }
              label="SpotGamma IV"
              labelPlacement={
                selectedDisplayType === ExpirationsDisplayType.ExpirationDate
                  ? 'top'
                  : 'start'
              }
            />
          )}
        </FormGroup>
      </FormControl>

      {showTermStructureIVStats && (
        <StatsLookbackSettings
          value={currentStatsLookback}
          onChange={onStatsLookbackChange}
        />
      )}

      <Typography sx={{ fontSize: '12px', marginBottom: '4px' }}>
        Add Additional Dates to Compare
      </Typography>
      <Stack direction="row" spacing={5}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            views={['day']}
            label="From Date"
            value={selectedDate}
            disabled={reachedMaximumDates}
            onChange={(newValue: Dayjs | null) => {
              if (newValue) {
                setSelectedDate(newValue);
              }
            }}
            shouldDisableDate={(date: Dayjs) =>
              selectedDates.some((dailyGreeks: DailyGreeks) =>
                dailyGreeks.tradeDate.isSame(date, 'day'),
              ) ||
              !isBusinessDay(date) ||
              date.isBefore(MIN_QUERY_DATE, 'day') ||
              !isMarketOpenOnDate(date)
            }
            format="YYYY-MM-DD"
            minDate={MIN_QUERY_DATE}
            maxDate={maxQueryDate}
            slotProps={{
              ...defaultSlotProps,
              textField: {
                ...defaultSlotProps.textField,
                style: {
                  width: '180px',
                  marginBottom: '12px',
                  fontSize: '14px',
                },
                helperText: datePickerHelpText,
                error: error != null,
              },
            }}
          />
        </LocalizationProvider>
        <Button
          variant="contained"
          size="small"
          disabled={loading || !isSelectedDateValid || reachedMaximumDates}
          sx={{
            width: '45px',
            height: '37px',
            marginLeft: '20px',
            fontFamily: 'Satoshi Complete',
            fontSize: 14,
            textTransform: 'capitalize',
          }}
          onClick={() => {
            onAddNewTimeFrame(selectedDate);
          }}
        >
          {loading ? (
            <CircularProgress
              style={{
                color: theme.palette.getContrastText(
                  theme.palette.background.default,
                ),
              }}
            />
          ) : (
            'Add'
          )}
        </Button>
      </Stack>
      {selectedDates.length > 0 ? (
        <Divider
          style={{
            borderColor: theme.palette.getContrastText(
              theme.palette.background.default,
            ),
          }}
        />
      ) : null}
      <List style={{ marginTop: 0 }}>
        {selectedDates.map((dailyGreeks: DailyGreeks) => {
          return (
            <TimeFramePill
              key={dailyGreeks.key}
              identifier={dailyGreeks.key}
              date={dailyGreeks.tradeDate}
              visibleLines={visibleChartLines}
              onToggleLineVisiblity={onToggleLineVisiblity}
              onDelete={onDeleteTimeFrame}
              icon={<ShowChartIcon />}
              bgColor={dailyGreeks.color}
            />
          );
        })}
      </List>
    </Stack>
  );
};

export const TermStructureChartSettings = () => {
  return (
    <SettingsPopout
      title="Term Structure Settings"
      popperID="term-structure-controls"
      placement="bottom-end"
    >
      <TermStructureControls />
    </SettingsPopout>
  );
};

export const TermStructureZoomOutButton = () => {
  const { zoomConfig, setZoomConfig, chartData } = useTermStructureCtx();
  return (
    <ZoomOutButton
      zoomConfig={zoomConfig}
      setZoomConfig={setZoomConfig}
      initialData={chartData}
      overrideDefault={{
        leftIdx: DEFAULT_BRUSH_ZOOM_CONFIG.leftIdx,
        rightIdx: chartData.length - 1,
      }}
    />
  );
};

export const TermStructureDownloadButton = () => {
  const { selectedSym, chartRef } = useTermStructureCtx();
  return (
    <DownloadChartButton
      chartRef={chartRef}
      selectedSym={selectedSym}
      chartName="Term-Structure"
    />
  );
};
